/**
 * InspectionCleaning タブ
 *
 * Phase 18: 設備点検タブ廃止 — 全ての定期点検情報を「管理業務 (ManagementTask)」に内包する。
 *  - 旧「設備点検サブタブ」(InspectionScheduleMatrix / InspectionRecord ベース) は削除
 *  - 「管理業務マトリクス」のみを表示し、`linked_assets` 経由で設備/区画と紐付ける
 *  - 法定点検フラグは管理業務マスタ (ManagementTask.is_legal_inspection) に集約。
 *    リモート点検 (is_remote) のみ月別実績 (ManagementTaskResult) で
 *    予定 / 実績を別々に管理する
 *  - 月別判定 (合格/条件付合格/不合格) と指摘事項は ManagementTaskResult 側に集約済み
 *
 * libs 側はステートレス。本ファイルが Redux と libs の橋渡し役。
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  CardTab,
  CustomButton,
  CustomModal,
  CustomSectionTitle,
  CustomSingleSelect,
  ManagementTaskMatrix,
  colors,
  type ManagementCellState,
  type ManagementTask as ManagementTaskVM,
  type ManagementTaskResult as ManagementTaskResultVM,
  type ManagementFrequency,
  type ManagementTaskType,
  type ManagementFrequencyUnit,
  type ManagementTaskTargetArea,
  type InspectionResultType,
} from "@ippon/shared-components";

import type { AppDispatch, RootState } from "@/store";
import {
  fetchManagementTasks,
  createManagementTask,
  updateManagementTask,
  deleteManagementTask,
  type ManagementTaskRecord,
  type LinkedUnit,
} from "@/store/managementTaskSlice";
import { fetchTaskResults } from "@/store/managementTaskResultSlice";
// 旧 fetchEquipment / fetchPropertySectionsByProperty は廃止。
// linked 対象は Unit に統一されたため、必要なら fetchUnitsByProperty を使う。
import { fetchUnitsByProperty } from "@/store/unitSlice";
import { fetchManagementTaskCustomFields } from "@/store/managementTaskCustomFieldSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { getApiErrorType, type ApiErrorType } from "@/utils/apiErrorType";
import { buildFiscalYearOptions } from "@/utils/fiscalYearOptions";
import { useStaleInitialLoadErrorToast } from "@/utils/initialLoadError";
import { Can } from "@/components/auth/Can";

import ManagementTaskEditModal from "./drawers/ManagementTaskEditModal";
import ManagementPreviewDrawer, {
  type ManagementPreviewMode,
} from "./drawers/ManagementPreviewDrawer";

interface Props {
  propertyId: string;
  /** PreviewDrawer から /management-tasks/:id へ全画面遷移時のパンくず用 */
  propertyName?: string;
}

// ──────────────────────────────────────────
// Helpers: Redux 型 → libs ViewModel 型 変換
// ──────────────────────────────────────────

const toTaskVM = (rec: ManagementTaskRecord, no: number): ManagementTaskVM => ({
  id: rec.task_id,
  no: rec.no || no,
  name: rec.name,
  taskType: rec.task_type as ManagementTaskType,
  frequency: rec.frequency as ManagementFrequency,
  frequencyCount: rec.frequency_count,
  frequencyUnit: rec.frequency_unit as ManagementFrequencyUnit,
  description: rec.description,
  relatedLaws: rec.related_laws,
  contractor: rec.contractor,
  targetAreas: (rec.target_areas || []) as ManagementTaskTargetArea[],
  remarks: rec.remarks,
  linkedAssets: (rec.linked_units || []).map((u: LinkedUnit) => ({
    id: u.unit_id,
    type: u.area_type === "facility" ? "equipment" : "area",
    name: u.name,
  })),
  // 「法定」chip 表示用。task_type==="点検" のときだけ意味を持つ。
  isLegal: rec.task_type === "点検" ? !!rec.is_legal_inspection : false,
});

// ──────────────────────────────────────────
// 管理業務 (単一タブ)
// ──────────────────────────────────────────

const ManagementTaskTab: React.FC<{
  propertyId: string;
  propertyName?: string;
  year: number;
  yearSelectEl?: React.ReactNode;
}> = ({ propertyId, propertyName, year, yearSelectEl }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const tasks = useSelector((s: RootState) => s.managementTasks.list.data);
  const tasksLoading = useSelector(
    (s: RootState) => s.managementTasks.list.loading,
  );
  const tasksLoaded = useSelector(
    (s: RootState) => s.managementTasks.list.loaded,
  );
  const results = useSelector(
    (s: RootState) => s.managementTaskResults.list.data,
  );
  const resultsLoaded = useSelector(
    (s: RootState) => s.managementTaskResults.list.loaded,
  );

  // 会計年度の開始月 = 決算月 + 1 (12 月決算なら 1 月)
  const fiscalClosingMonth: number =
    useSelector(
      (s: RootState) =>
        (s.clients?.detailed?.data as any)?.fiscal_closing_month,
    ) ?? 3;
  const fiscalYearStartMonth: number =
    fiscalClosingMonth === 12 ? 1 : fiscalClosingMonth + 1;

  const [taskTypeFilter, setTaskTypeFilter] = useState<
    "all" | ManagementTaskType
  >("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // 詳細/月別を 1 つのプレビュードロワーで出し分ける。タスク⇔各月を行き来しても
  // ドロワーは開いたまま中身だけ差し替わる (物件構成のドロワーと同じ挙動)。
  const [previewMode, setPreviewMode] = useState<ManagementPreviewMode | null>(
    null,
  );
  const [cellState, setCellState] = useState<ManagementCellState | null>(null);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  // タスク新規登録モーダル (新規はモーダル / 詳細はページ + プレビュードロワー)
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);

  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInitialLoadError(null);
      try {
        const results = await Promise.all([
          dispatch(fetchManagementTasks({ property_id: propertyId })),
          dispatch(fetchTaskResults({ property_id: propertyId, year })),
          dispatch(fetchUnitsByProperty({ property_id: propertyId })),
          dispatch(fetchManagementTaskCustomFields()),
        ]);
        if (cancelled) return;
        const rejected = results.find((r) =>
          (r as { type?: string }).type?.endsWith("/rejected"),
        );
        if (rejected) {
          setInitialLoadError(
            getApiErrorType((rejected as any).error ?? rejected) ?? "unknown",
          );
        }
      } catch (err) {
        if (!cancelled) setInitialLoadError(getApiErrorType(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, propertyId, year]);

  const hasInspectionCache =
    tasksLoaded || resultsLoaded || tasks.length > 0 || results.length > 0;
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasInspectionCache,
    dedupeKey: `inspection:${propertyId}:${year}`,
  });

  const baseTaskVMs = useMemo<ManagementTaskVM[]>(
    () => tasks.map((t, idx) => toTaskVM(t, idx + 1)),
    [tasks],
  );
  // ローカル並び替え順 (DnD で更新される task_id の配列)
  const [taskOrder, setTaskOrder] = useState<string[] | null>(null);
  const taskVMs = useMemo<ManagementTaskVM[]>(() => {
    const ordered: ManagementTaskVM[] = (() => {
      if (!taskOrder) return baseTaskVMs;
      const map = new Map(baseTaskVMs.map((t) => [t.id, t]));
      const arr = taskOrder
        .map((id) => map.get(id))
        .filter((t): t is ManagementTaskVM => Boolean(t));
      // 漏れ (新規タスク等) は末尾に追加
      for (const t of baseTaskVMs) {
        if (!taskOrder.includes(t.id)) arr.push(t);
      }
      return arr;
    })();
    // 並び順に合わせて `no` を再採番 (1始まり)
    return ordered.map((t, idx) => ({ ...t, no: idx + 1 }));
  }, [baseTaskVMs, taskOrder]);
  // 会計年度範囲判定: year=2025 + startMonth=4 なら 2025/4〜2026/3 が範囲
  const isInFiscalYear = useMemo(
    () => (rYear: number, rMonth: number) => {
      if (fiscalYearStartMonth === 1) return rYear === year;
      if (rYear === year) return rMonth >= fiscalYearStartMonth;
      if (rYear === year + 1) return rMonth < fiscalYearStartMonth;
      return false;
    },
    [year, fiscalYearStartMonth],
  );

  const resultVMs = useMemo<ManagementTaskResultVM[]>(
    () =>
      results
        .filter((r) => isInFiscalYear(r.year, r.month))
        .map((r) => ({
          taskId: r.task_id,
          month: r.month,
          planned: r.planned,
          plannedDates: r.planned_dates,
          completed: r.completed,
          executionRecords: (r.execution_records || []).map((rec) => ({
            executionId: rec.execution_id,
            date: rec.date,
            time: rec.time,
            checker: rec.checker,
            result: rec.result,
            findings: rec.findings,
            note: rec.note,
            findingStatus: rec.finding_status,
            findingAction: rec.finding_action,
            findingResolvedDate: rec.finding_resolved_date,
            attachments: rec.attachments?.map((a) => ({
              attachmentId: a.attachment_id,
              fileName: a.file_name,
              s3Key: a.s3_key,
              uploadedAt: a.uploaded_at,
            })),
          })),
          totalJudgement: r.total_judgement as InspectionResultType | undefined,
          remarks: r.remarks,
          otherAttachments: r.other_attachments?.map((a) => ({
            attachmentId: a.attachment_id,
            fileName: a.file_name,
            s3Key: a.s3_key,
            uploadedAt: a.uploaded_at,
          })),
          plannedIsRemote: r.planned_is_remote,
          actualIsRemote: r.actual_is_remote,
        })),
    [results, isInFiscalYear],
  );

  // 各月セルクリック → 同じドロワーの中身を月別実績へ差し替え (開いたまま切替)。
  const handleCellClick = (state: ManagementCellState) => {
    // 新規作成モーダルを開いている時のみブロック (未保存入力の保護)。
    if (taskCreateOpen) return;
    setCellState(state);
    setPreviewMode("result");
  };

  // タスククリック → 同じドロワーの中身を管理業務詳細へ差し替え (開いたまま切替)。
  const handleEditTask = (vm: ManagementTaskVM) => {
    if (taskCreateOpen) return;
    setDetailTaskId(vm.id);
    setPreviewMode("task");
  };
  const handleCreateTask = () => {
    if (previewMode) return;
    setTaskCreateOpen(true);
  };

  // ── 種別 CardTab ──
  const TASK_TYPE_KEYS: ("all" | ManagementTaskType)[] = [
    "all",
    "清掃",
    "点検",
    "検査",
    "調査",
    "業務",
    "試験",
    "その他",
  ];
  const [cardTabActive, setCardTabActive] = useState(0);
  useEffect(() => {
    setTaskTypeFilter(TASK_TYPE_KEYS[cardTabActive]);
  }, [cardTabActive]);

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: tasks.length,
      清掃: 0,
      点検: 0,
      検査: 0,
      調査: 0,
      業務: 0,
      試験: 0,
      その他: 0,
    };
    for (const t of tasks) {
      const k = t.task_type;
      if (k && counts[k] !== undefined) counts[k] += 1;
    }
    return counts;
  }, [tasks]);

  const cardTabs = [
    {
      key: "all",
      label: "すべて",
      count: tabCounts.all,
      hasNotification: false,
    },
    {
      key: "清掃",
      label: "清掃",
      count: tabCounts["清掃"],
      hasNotification: false,
    },
    {
      key: "点検",
      label: "点検",
      count: tabCounts["点検"],
      hasNotification: false,
    },
    {
      key: "検査",
      label: "検査",
      count: tabCounts["検査"],
      hasNotification: false,
    },
    {
      key: "調査",
      label: "調査",
      count: tabCounts["調査"],
      hasNotification: false,
    },
    {
      key: "業務",
      label: "業務",
      count: tabCounts["業務"],
      hasNotification: false,
    },
    {
      key: "試験",
      label: "試験",
      count: tabCounts["試験"],
      hasNotification: false,
    },
    {
      key: "その他",
      label: "その他",
      count: tabCounts["その他"],
      hasNotification: false,
    },
  ];
  const cardTabData = {
    all: { data: [], pagination: { total: tabCounts.all, page: 1, limit: 0 } },
    清掃: {
      data: [],
      pagination: { total: tabCounts["清掃"], page: 1, limit: 0 },
    },
    点検: {
      data: [],
      pagination: { total: tabCounts["点検"], page: 1, limit: 0 },
    },
    検査: {
      data: [],
      pagination: { total: tabCounts["検査"], page: 1, limit: 0 },
    },
    調査: {
      data: [],
      pagination: { total: tabCounts["調査"], page: 1, limit: 0 },
    },
    業務: {
      data: [],
      pagination: { total: tabCounts["業務"], page: 1, limit: 0 },
    },
    試験: {
      data: [],
      pagination: { total: tabCounts["試験"], page: 1, limit: 0 },
    },
    その他: {
      data: [],
      pagination: { total: tabCounts["その他"], page: 1, limit: 0 },
    },
  };

  // 月セル編集に必要だった編集中タスク情報 (editingTaskName/Frequency/Type, editingResult,
  // editingResultRaw) は ManagementTaskResultDetailPage が独自に fetch するため不要。

  const handleSaveTaskNew = async (
    body: import("@/store/managementTaskSlice").CreateManagementTaskBody,
  ) => {
    const result = await dispatch(createManagementTask(body));
    if (createManagementTask.fulfilled.match(result)) {
      showToast({ type: "success", message: "登録しました" });
      return;
    }
    // 失敗時は throw してモーダル側の catch でトーストを出し、モーダルを閉じない。
    throw new Error((result.payload as string) || "登録に失敗しました");
  };
  const handleTaskUpdated = () => {
    dispatch(fetchManagementTasks({ property_id: propertyId }));
  };
  // 月実績の保存処理は ManagementTaskResultDetailPage 側に移管済み
  // (旧 handleSaveResult → page 内 handleSave)。

  // 並び替え (工事一覧のステータスと同じ方針):
  //   - 即座に setTaskOrder で UI 反映 (描画はローカル優先)
  //   - PATCH は background で投げるだけ。成功時は refetch しない (チラつき防止)
  //     → updateManagementTask.fulfilled が tasks[i].no を更新するので、
  //       次回マウント (リロード / 他ページから戻る) 時に BE 順で正しく描画される
  //   - 失敗時のみ toast + refetch で BE 順に戻す
  const handleReorder = (orderedIds: string[]) => {
    setTaskOrder(orderedIds);
    const tasksMap = new Map(tasks.map((t) => [t.task_id, t]));
    const toUpdate = orderedIds
      .map((id, idx) => ({ id, newNo: idx + 1 }))
      .filter(({ id, newNo }) => {
        const rec = tasksMap.get(id);
        return rec && rec.no !== newNo;
      });
    if (toUpdate.length === 0) return;
    Promise.all(
      toUpdate.map(({ id, newNo }) =>
        dispatch(
          updateManagementTask({
            property_id: propertyId,
            task_id: id,
            body: { no: newNo },
          }),
        ).unwrap(),
      ),
    ).catch((e) => {
      showToast({
        type: "error",
        message:
          (e as { message?: string })?.message ||
          "並び替えの保存に失敗しました",
      });
      void dispatch(fetchManagementTasks({ property_id: propertyId }));
      setTaskOrder(null);
    });
  };

  // 一括削除
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((task_id) =>
          dispatch(
            deleteManagementTask({ property_id: propertyId, task_id }),
          ).unwrap(),
        ),
      );
      showToast({ type: "success", message: "削除しました" });
      setSelectedIds([]);
      setOpenDeleteModal(false);
    } catch (e) {
      showToast({
        type: "error",
        message: e instanceof Error ? e.message : "削除に失敗しました",
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(taskVMs.map((t) => t.id));
    } else {
      setSelectedIds([]);
    }
  };
  const handleToggleOne = (taskId: string) => {
    setSelectedIds((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId],
    );
  };

  return (
    <>
      <CardTab
        tabs={cardTabs}
        tabData={cardTabData}
        activeTab={cardTabActive}
        loading={tasksLoading}
        loadingDelayMs={150}
        onTabChange={(idx: number) => setCardTabActive(idx)}
        renderContent={() => (
          <Box sx={{ pt: 1 }}>
            {/* 凡例 + コントロール */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="flex-end"
              sx={{
                mb: 1,
                color: "text.secondary",
                flexWrap: "wrap",
              }}
            >
              <Stack
                direction="row"
                spacing={2.5}
                alignItems="center"
                flexWrap="wrap"
                sx={{ pb: 0.5 }}
              >
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid",
                      borderColor: "primary.main",
                      boxSizing: "border-box",
                    }}
                  />
                  <Box sx={{ fontSize: "11px" }}>予定あり</Box>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                    }}
                  />
                  <Box sx={{ fontSize: "11px" }}>実施済</Box>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid",
                      borderColor: "primary.main",
                      boxSizing: "border-box",
                      color: "primary.main",
                      fontSize: "8px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    R
                  </Box>
                  <Box sx={{ fontSize: "11px" }}>リモート点検予定</Box>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "#fff",
                      fontSize: "8px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    R
                  </Box>
                  <Box sx={{ fontSize: "11px" }}>リモート点検実施済</Box>
                </Stack>
              </Stack>
              <Box sx={{ flex: 1 }} />
              {yearSelectEl}
              <Can action="management-task:edit">
                <Box data-testid="management-task-add-button">
                  <CustomButton
                    label="項目追加"
                    startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                    onClick={handleCreateTask}
                    buttonCategory="submit"
                  />
                </Box>
              </Can>
              <Can action="management-task:delete">
                <Box data-testid="management-task-bulk-delete-button">
                  <CustomButton
                    label="削除"
                    buttonCategory="delete"
                    onClick={() => setOpenDeleteModal(true)}
                    disabled={selectedIds.length === 0 || bulkDeleting}
                  />
                </Box>
              </Can>
            </Stack>
            <ManagementTaskMatrix
              tasks={taskVMs}
              results={resultVMs}
              year={year}
              startMonth={fiscalYearStartMonth}
              taskTypeFilter={taskTypeFilter}
              onEditTask={handleEditTask}
              onCellClick={handleCellClick}
              selectable
              selectedTaskIds={selectedIds}
              onToggleTaskSelect={handleToggleOne}
              onToggleAll={handleToggleAll}
              onReorder={handleReorder}
              loading={tasksLoading}
              noDataMessage="管理業務が登録されていません"
              noDataIcon={
                <FactCheckOutlinedIcon
                  sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
                />
              }
            />
          </Box>
        )}
      />
      {/* 管理業務詳細(task)と月別実績(result)を 1 つのプレビュードロワーで出し分け。
          タスク⇔各月を行き来してもドロワーは開いたまま中身だけ差し替わる。
          page 内で task + result を fetch するので識別キーだけ渡せばよい。 */}
      <ManagementPreviewDrawer
        open={previewMode !== null}
        onClose={() => setPreviewMode(null)}
        mode={previewMode}
        taskId={
          previewMode === "result" ? (cellState?.taskId ?? null) : detailTaskId
        }
        year={year}
        month={cellState?.month ?? 0}
        propertyId={propertyId}
        propertyName={propertyName}
        onTaskUpdated={handleTaskUpdated}
        onResultUpdated={() => {
          // 結果保存後の月セル表示(planned/completed)の最新化は、year 変更時に走る
          // 既存の fetch effect でカバーされるため、ここでの明示的な再取得は不要。
        }}
      />
      {/* 新規登録モーダル (詳細はページ + プレビュードロワーで開く) */}
      <ManagementTaskEditModal
        open={taskCreateOpen}
        onClose={() => setTaskCreateOpen(false)}
        task={null}
        property_id={propertyId}
        defaultTaskType={taskTypeFilter === "all" ? "" : taskTypeFilter}
        onSaveExisting={async () => {}}
        onSaveNew={handleSaveTaskNew}
      />
      {/* 一括削除確認モーダル */}
      <CustomModal
        title="削除の確認"
        openModal={openDeleteModal}
        handleCloseModal={() => setOpenDeleteModal(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box data-testid="management-task-delete-confirm-modal">
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した{selectedIds.length}
            件の管理業務を削除してもよろしいでしょうか？
            <br />
            削除されたデータは復元できません。
          </Typography>
          {/* CustomButton (cancelModal / deleteModal) は width:50% を持つので
              親 flex container に直接配置する (他の削除モーダルと同じ構造) */}
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}
            data-testid="management-task-delete-modal-actions"
          >
            <CustomButton
              label="戻る"
              onClick={() => setOpenDeleteModal(false)}
              buttonCategory="cancelModal"
            />
            <Can action="management-task:delete">
              <CustomButton
                label="削除"
                onClick={handleBulkDelete}
                buttonCategory="deleteModal"
                disabled={bulkDeleting}
              />
            </Can>
          </Box>
        </Box>
      </CustomModal>
    </>
  );
};

// ──────────────────────────────────────────
// メイン (単一タブ — CardTab 撤去)
// ──────────────────────────────────────────

const InspectionCleaning: React.FC<Props> = ({ propertyId, propertyName }) => {
  // 会計年度起算年で初期化 (3月決算で 2026/02 → 2025年度)
  const fiscalClosingMonth: number =
    useSelector(
      (s: RootState) =>
        (s.clients?.detailed?.data as any)?.fiscal_closing_month,
    ) ?? 3;
  const fiscalYearStartMonth: number =
    fiscalClosingMonth === 12 ? 1 : fiscalClosingMonth + 1;
  const currentFiscalYear = useMemo(() => {
    const now = new Date();
    return now.getMonth() + 1 >= fiscalYearStartMonth
      ? now.getFullYear()
      : now.getFullYear() - 1;
  }, [fiscalYearStartMonth]);

  const [year, setYear] = useState(currentFiscalYear);

  const {
    control: yearControl,
    watch: yearWatch,
    reset: resetYearForm,
    formState: { errors: yearErrors },
  } = useForm<{ year: string }>({
    defaultValues: { year: String(currentFiscalYear) },
  });

  // fiscal_closing_month が非同期で取れた初回 1 度だけ defaultYear に追従
  const yearInitSyncedRef = useRef(false);
  useEffect(() => {
    if (!yearInitSyncedRef.current) {
      setYear(currentFiscalYear);
      resetYearForm({ year: String(currentFiscalYear) });
      yearInitSyncedRef.current = true;
    }
  }, [currentFiscalYear, resetYearForm]);

  useEffect(() => {
    const sub = yearWatch((v) => {
      if (v.year) setYear(Number(v.year));
    });
    return () => sub.unsubscribe();
  }, [yearWatch]);

  const fiscalYearOptions = useMemo(
    () => buildFiscalYearOptions(currentFiscalYear),
    [currentFiscalYear],
  );

  const yearSelectEl = (
    <Box sx={{ minWidth: 120 }} data-testid="management-task-year-select">
      <CustomSingleSelect
        label=""
        name="year"
        control={yearControl}
        errors={yearErrors}
        options={fiscalYearOptions}
        inputWidth="100%"
        inputWidthSp="100%"
        hasSpacing={false}
      />
    </Box>
  );

  return (
    <Box sx={{ pb: 10 }}>
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle title="点検/清掃管理" />
      </Box>
      <Box sx={{ mt: 2 }}>
        <ManagementTaskTab
          propertyId={propertyId}
          propertyName={propertyName}
          year={year}
          yearSelectEl={yearSelectEl}
        />
      </Box>
    </Box>
  );
};

export default InspectionCleaning;
